import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box} from "@mui/material";
import {
    Printout,
    PrintoutOrientation,
    PrintoutType,
} from "crqlar-react-common/dist/models/printout/printout.model";
import {EditorContent, useEditor} from "@tiptap/react";
import type {Editor} from "@tiptap/core";
import {NodeSelection} from "@tiptap/pm/state";
import InvisibleCharacters from "@tiptap/extension-invisible-characters";
import {ListKeymap} from "@tiptap/extension-list";
import {UndoRedo} from "@tiptap/extensions";
import {PrintoutPagination} from "../pagination/PrintoutPagination";
import {
    getPrintoutOverallWidth,
    getPrintoutPageSize,
    getPrintoutPaginationOptions,
    type PrintoutPageSize,
} from "../core/printoutPageGeometry";
import {parsePrintoutContent, serializePrintoutContent} from "../core/printoutDocument";
import PrintoutEditorToolbar from "./toolbar/PrintoutEditorToolbar";
import {getPrintoutFontSx} from "../core/printoutFonts";
import {usePrintoutFontStylesheet} from "../core/usePrintoutFontStylesheet";
import {printoutDocumentTypographySx} from "../core/printoutTypography";
import {PrintoutSelectionKeymap} from "./PrintoutSelectionKeymap";
import {PrintoutDragAndDrop} from "./dnd/PrintoutDragAndDrop";
import {printoutDragFeedbackSx} from "./dnd/printoutDragStyles";
import {PrintoutVariableSlashCommand} from "../variables/PrintoutVariableSlashCommand";
import {
    selectedVariableChipSx,
    variableChipSx,
} from "../../common/variables/variableChipStyles";
import {
    createPrintoutBaseExtensions,
    editorPrintoutBlockChromeSx,
    sharedPrintoutBlockBodySx,
} from "../blocks";
import {
    loadPrintoutEditorDocument,
    setPrintoutEditorDocument,
    withoutPrintoutTrailingBodyParagraph,
    withPrintoutTrailingBodyParagraph,
} from "./loadPrintoutEditorDocument";
import {PrintoutTrailingBodyParagraph} from "./PrintoutTrailingBodyParagraph";

interface Props {
    content: string;
    documentKey: string;
    printout: Printout;
    onActivationChange?: (active: boolean) => void;
    onContentChange: (content: string) => void;
    onEditorChange?: (editor: Editor | null) => void;
    onReady?: () => void;
}

interface PageBounds {
    height: number;
    width: number;
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.25;
const FIT_CANVAS_GUTTER = 48;

const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

const roundZoom = (zoom: number) => Math.round(zoom * 100) / 100;

const getInitialPageBounds = (pageSize: PrintoutPageSize): PageBounds => ({
    height: pageSize.pageHeight + 2,
    width: getPrintoutOverallWidth(pageSize) + 2,
});

const getEditorExtensions = (
    orientation?: PrintoutOrientation,
    printoutType: PrintoutType | null = null,
) => [
    ...createPrintoutBaseExtensions(),
    PrintoutSelectionKeymap,
    PrintoutDragAndDrop,
    PrintoutTrailingBodyParagraph,
    ListKeymap.configure({
        listTypes: [
            {
                itemName: 'listItem',
                wrapperNames: ['bulletList', 'orderedList'],
            },
        ],
    }),
    UndoRedo,
    InvisibleCharacters.configure({
        visible: true,
    }),
    PrintoutVariableSlashCommand.configure({
        printoutType,
    }),
    PrintoutPagination.configure(getPrintoutPaginationOptions(orientation)),
];

const PrintoutEditorCanvas = ({
    content,
    documentKey,
    printout,
    onActivationChange,
    onContentChange,
    onEditorChange,
    onReady,
}: Props) => {

    usePrintoutFontStylesheet(printout.fontUrl);
    const onContentChangeRef = useRef(onContentChange);
    const externalContentRef = useRef(content);
    const loadedDocumentKeyRef = useRef(documentKey);
    const canvasRef = useRef<HTMLDivElement | null>(null);
    const parsedContent = useMemo(
        () => withPrintoutTrailingBodyParagraph(parsePrintoutContent(content)),
        [content],
    );

    const printoutType = printout.type ?? null;
    const editorExtensions = useMemo(
        () => getEditorExtensions(printout.orientation, printoutType),
        [printout.orientation, printoutType],
    );
    const paginationPageSize = useMemo(() => getPrintoutPageSize(printout.orientation), [printout.orientation]);
    const fontStyles = useMemo(() => getPrintoutFontSx(
        printout.fontFamily,
        parsedContent.attrs?.headingFontFamily,
    ), [parsedContent, printout.fontFamily]);
    const [editorActivated, setEditorActivated] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [pageBounds, setPageBounds] = useState<PageBounds>(() => getInitialPageBounds(paginationPageSize));

    useEffect(() => {
        onContentChangeRef.current = onContentChange;
    }, [onContentChange]);

    const zoomOut = useCallback(() => {
        setZoom((currentZoom) => clampZoom(roundZoom(currentZoom - ZOOM_STEP)));
    }, []);

    const zoomIn = useCallback(() => {
        setZoom((currentZoom) => clampZoom(roundZoom(currentZoom + ZOOM_STEP)));
    }, []);

    const fitZoomToCanvas = useCallback(() => {
        const availableWidth = canvasRef.current?.clientWidth ?? 0;

        if (!availableWidth || !pageBounds.width) return;

        setZoom(clampZoom(roundZoom((availableWidth - FIT_CANVAS_GUTTER) / pageBounds.width)));
    }, [pageBounds.width]);

    const editor = useEditor({
        autofocus: false,
        immediatelyRender: false,
        extensions: editorExtensions,
        content: parsedContent,
        editorProps: {
            attributes: {
                class: 'printout-tiptap-editor',
            },
            // Prevent printable input from replacing a selected atom block.
            handleKeyDown: (view, event) => {
                if (event.ctrlKey || event.metaKey || event.altKey) return false;
                if (event.key.length !== 1) return false;

                const {selection} = view.state;

                return selection instanceof NodeSelection && selection.node.isBlock;
            },
        },
        onUpdate: ({editor}) => {
            const nextContent = serializePrintoutContent(
                withoutPrintoutTrailingBodyParagraph(editor.getJSON()),
            );
            externalContentRef.current = nextContent;
            onContentChangeRef.current(nextContent);
        },
        onCreate: ({editor}) => {
            editor.commands.blur();
            onReady?.();
        },
        onFocus: () => {
            setEditorActivated(true);
            onActivationChange?.(true);
        },
    }, [editorExtensions]);

    const activeEditor = editor && !editor.isDestroyed ? editor : null;

    // useEditor may replace its instance before effects run, including during Fast Refresh.
    useEffect(() => {
        onEditorChange?.(activeEditor?.isDestroyed ? null : activeEditor);

        return () => {
            onEditorChange?.(null);
        };
    }, [activeEditor, onEditorChange]);

    useEffect(() => {
        setEditorActivated(false);
        onActivationChange?.(false);
        activeEditor?.commands.blur();
    }, [activeEditor, documentKey, onActivationChange]);

    const updatePageBounds = useCallback(() => {
        if (!activeEditor || activeEditor.isDestroyed) return;

        const editorElement = activeEditor.view.dom;

        if (!editorElement) return;

        const nextWidth = editorElement.offsetWidth;
        const nextHeight = editorElement.offsetHeight;

        if (!nextWidth || !nextHeight) return;

        setPageBounds((currentBounds) => {
            if (currentBounds.width === nextWidth && currentBounds.height === nextHeight) {
                return currentBounds;
            }

            return {
                height: nextHeight,
                width: nextWidth,
            };
        });
    }, [activeEditor]);

    useEffect(() => {
        if (!activeEditor || activeEditor.isDestroyed) return;

        activeEditor.commands.setPrintoutVariableCatalog({printoutType});
    }, [activeEditor, printoutType]);

    useEffect(() => {
        if (!activeEditor || activeEditor.isDestroyed) return;

        activeEditor.commands.updatePrintoutPagination(getPrintoutPaginationOptions(printout.orientation));
    }, [
        activeEditor,
        paginationPageSize.marginBottom,
        paginationPageSize.marginLeft,
        paginationPageSize.marginRight,
        paginationPageSize.marginTop,
        paginationPageSize.pageHeight,
        paginationPageSize.pageWidth,
        printout.orientation,
    ]);

    useEffect(() => {
        setPageBounds({
            height: paginationPageSize.pageHeight + 2,
            width: paginationPageSize.pageWidth + paginationPageSize.marginLeft + paginationPageSize.marginRight + 2,
        });
    }, [
        paginationPageSize.marginLeft,
        paginationPageSize.marginRight,
        paginationPageSize.pageHeight,
        paginationPageSize.pageWidth,
    ]);

    // Defer layout-changing state writes to avoid ResizeObserver loop errors in CRA.
    useEffect(() => {
        if (!activeEditor || activeEditor.isDestroyed) return;

        const editorElement = activeEditor.view.dom;
        let scheduledFrame = 0;

        const scheduleUpdatePageBounds = () => {
            if (scheduledFrame) return;

            scheduledFrame = requestAnimationFrame(() => {
                scheduledFrame = 0;
                updatePageBounds();
            });
        };

        const resizeObserver = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(scheduleUpdatePageBounds);

        updatePageBounds();
        resizeObserver?.observe(editorElement);
        window.addEventListener('resize', scheduleUpdatePageBounds);

        return () => {
            if (scheduledFrame) cancelAnimationFrame(scheduledFrame);

            resizeObserver?.disconnect();
            window.removeEventListener('resize', scheduleUpdatePageBounds);
        };
    }, [activeEditor, updatePageBounds]);

    useEffect(() => {
        if (!activeEditor) return;

        const documentChanged = loadedDocumentKeyRef.current !== documentKey;
        if (!documentChanged && content === externalContentRef.current) return;

        const animationFrame = window.requestAnimationFrame(() => {
            if (activeEditor.isDestroyed) return;

            const nextContent = parsePrintoutContent(content);
            const currentContent = serializePrintoutContent(
                withoutPrintoutTrailingBodyParagraph(activeEditor.getJSON()),
            );
            const serializedNextContent = serializePrintoutContent(nextContent);

            externalContentRef.current = content;
            loadedDocumentKeyRef.current = documentKey;

            if (documentChanged) {
                loadPrintoutEditorDocument(activeEditor, nextContent);
            } else if (serializedNextContent !== currentContent) {
                setPrintoutEditorDocument(activeEditor, nextContent, false);
            }

            if (!documentChanged) {
                activeEditor.view.dispatch(activeEditor.state.tr.setMeta("addToHistory", false));
            }
        });

        return () => window.cancelAnimationFrame(animationFrame);
    }, [activeEditor, content, documentKey]);

    return (
        <Box
            sx={{
                bgcolor: 'grey.100',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <PrintoutEditorToolbar
                active={editorActivated}
                editor={activeEditor}
                printoutType={printoutType}
                zoomControls={{
                    canZoomIn: zoom < MAX_ZOOM,
                    canZoomOut: zoom > MIN_ZOOM,
                    onFitZoom: fitZoomToCanvas,
                    onZoomIn: zoomIn,
                    onZoomOut: zoomOut,
                }}
            />

            <Box
                data-printout-editor-active={editorActivated}
                ref={canvasRef}
                sx={[{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: 'auto',
                    px: 3,
                    py: 3,
                    '& .printout-zoom-frame': {
                        flexShrink: 0,
                        mx: 'auto',
                        position: 'relative',
                    },
                    '& .printout-zoom-content': {
                        left: 0,
                        position: 'absolute',
                        top: 0,
                        transform: `scale(${zoom})`,
                        transformOrigin: 'top left',
                    },
                    '& .printout-tiptap-editor': {
                        ...printoutDocumentTypographySx,
                        bgcolor: 'background.paper',
                        boxShadow: '0 18px 36px rgba(15, 23, 42, 0.12)',
                        boxSizing: 'content-box',
                        flexShrink: 0,
                        ...fontStyles,
                        outline: 'none',
                    },
                    '& .printout-tiptap-editor > *:last-child': {
                        mb: 0,
                    },
                    '& .printout-variable-node': {
                        display: 'inline-flex',
                        lineHeight: 'inherit',
                        position: 'relative',
                        verticalAlign: 'baseline',
                    },
                    '& .printout-variable-menu': {
                        alignItems: 'center',
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        bottom: 'calc(100% + 4px)',
                        boxShadow: '0 16px 34px rgba(15, 23, 42, 0.14)',
                        display: 'inline-flex',
                        left: 0,
                        maxWidth: 280,
                        p: 0.5,
                        position: 'absolute',
                        transform: 'translateX(var(--printout-menu-shift-x, 0px))',
                        whiteSpace: 'nowrap',
                        width: 'max-content',
                        zIndex: 12,
                    },
                    '& .printout-variable-menu[data-printout-menu-placement="below"]': {
                        bottom: 'auto',
                        top: 'calc(100% + 4px)',
                    },
                    '& .printout-variable-name-label': {
                        alignItems: 'center',
                        color: 'text.primary',
                        display: 'inline-flex',
                        fontSize: 13,
                        fontWeight: 600,
                        height: 28,
                        maxWidth: 150,
                        overflow: 'hidden',
                        px: 0.75,
                        textOverflow: 'ellipsis',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                    },
                    '& .printout-variable-menu-icon': {
                        borderRadius: 1,
                        color: 'text.secondary',
                        height: 28,
                        width: 28,
                    },
                    '& .printout-variable-chip': {
                        ...variableChipSx,
                    },
                    '& .printout-variable-chip:hover': {
                        cursor: 'default',
                    },
                    [[
                        '& .printout-variable-chip.is-selected',
                        '& .printout-variable-node.is-selected .printout-variable-chip',
                        '& .ProseMirror-selectednode.printout-variable-chip',
                        '& .ProseMirror-selectednode.printout-variable-node .printout-variable-chip',
                    ].join(', ')]: {
                        ...selectedVariableChipSx,
                    },
                    '& .printout-tiptap-editor .tiptap-invisible-character::before': {
                        color: '#CBD5E1',
                    },
                    '&[data-printout-editor-active="false"] .tiptap-invisible-character::before': {
                        display: 'none',
                    },
                    ...editorPrintoutBlockChromeSx,
                    ...sharedPrintoutBlockBodySx,
                }, printoutDragFeedbackSx]}
            >
                <Box
                    className="printout-zoom-frame"
                    sx={{
                        height: pageBounds.height * zoom,
                        width: pageBounds.width * zoom,
                    }}
                >
                    <Box className="printout-zoom-content">
                        <EditorContent editor={activeEditor}/>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default PrintoutEditorCanvas;
